import React from 'react';
import { Box, Container, Typography, Grid, Divider, Stack } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import CopyrightIcon from '@mui/icons-material/Copyright';

export default function LegalInformation() {
  const legalIcon = (
    <GavelIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const complianceIcon = (
    <VerifiedUserIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const termsIcon = (
    <DescriptionIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const copyrightIcon = (
    <CopyrightIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#080808",
        py: 10,
        position: "relative",
        color: "#e4e2e2"
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {legalIcon}
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography
              variant="h3"
              sx={{
                color: "#fefdfd",
                fontFamily: "'Spline Sans Mono', monospace",
                fontWeight: 600,
                fontStyle: "italic",
                fontSize: { xs: "24px", md: "32px" },
                lineHeight: "1.2",
                mb: 3
              }}
            >
              Terms and conditions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#7a7976",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "15px",
                lineHeight: "29.1px",
              }}
            >
              Last Updated: April 9, 2025
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ bgcolor: "#333", mb: 6 }} />

        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            1. Introduction
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
            }}
          >
            Welcome to our Secure Data Transfer Service. This Legal Information page provides important details about the legal aspects of using our services. By accessing or using our website and services, you agree to be bound by these terms and conditions, along with our Privacy Policy and other applicable policies. This document outlines your rights and responsibilities as a user and our obligations as a service provider.
          </Typography>
        </Box>

        {/* Feature cards */}
        <Grid container spacing={4} sx={{ mb: 6, flexWrap: {xs: "wrap", sm: "nowrap"}}}>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {termsIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                Our comprehensive terms outline the rules and guidelines for using our secure data transfer services.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {complianceIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Regulatory Compliance
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                We comply with industry standards and regulations to ensure secure and lawful data handling practices.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {copyrightIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Intellectual Property
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                Information regarding copyright, trademarks, and other intellectual property rights related to our service.
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        {/* Terms of Service */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            2. Terms of Service
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.1 Service Description
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 3
            }}
          >
            Our Secure Data Transfer Service provides users with tools and infrastructure to securely transfer, store, and manage digital files. We offer various subscription plans with different features, storage capacities, and support levels. By subscribing to our service, you gain access to our platform based on the terms of your selected plan.
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.2 User Responsibilities
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            As a user of our service, you agree to:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4, mb: 3 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Provide accurate and complete information when creating your account</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Maintain the security of your account credentials</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Use our services only for lawful purposes and in accordance with these terms</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Not attempt to circumvent or compromise our security systems</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Respect the intellectual property and privacy rights of others</Typography>
          </Box>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.3 Prohibited Activities
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            You may not use our service to:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Store or transfer illegal content</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Infringe on the intellectual property rights of others</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Distribute malware, viruses, or other harmful code</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Conduct unauthorized vulnerability testing or security assessments</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Engage in activities that may disrupt or impair our services</Typography>
          </Box>
        </Box>

        {/* Regulatory Compliance */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            3. Regulatory Compliance
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            Our service adheres to various regulations and standards, including:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Data Protection:</strong> We comply with GDPR, CCPA, and other applicable data protection laws. For more information, please refer to our Privacy Policy.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Information Security:</strong> Our security measures align with industry standards such as ISO 27001 and NIST frameworks to ensure the confidentiality, integrity, and availability of your data.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Electronic Communications:</strong> We adhere to regulations governing electronic communications, including opt-in requirements for marketing communications.
            </Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Financial Transactions:</strong> Our payment processing complies with PCI DSS standards to ensure secure handling of payment information.
            </Typography>
          </Box>
        </Box>

        {/* Intellectual Property */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            4. Intellectual Property
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            4.1 Our Intellectual Property
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 3
            }}
          >
            The content, features, and functionality of our website and services, including but not limited to text, graphics, logos, button icons, images, audio clips, data compilations, and software, are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, create derivative works, publicly display, republish, or distribute our content without our explicit permission.
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            4.2 User Content
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 3
            }}
          >
            You retain all rights to the content you upload, transfer, or store using our service. By using our service, you grant us a limited license to store, process, and transmit your content solely for the purpose of providing our services to you. We do not claim ownership of your content, and we will not use your content for any purposes other than providing and improving our services unless we obtain your explicit consent.
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            4.3 Copyright Infringement
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
            }}
          >
            We respect the intellectual property rights of others and expect our users to do the same. If you believe that your work has been copied in a way that constitutes copyright infringement, please provide us with the following information in accordance with the Digital Millennium Copyright Act (DMCA):
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>A description of the copyrighted work that you claim has been infringed</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>A description of where the material that you claim is infringing is located on our site</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Your contact information, including address, telephone number, and email</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>A statement by you that you have a good faith belief that the disputed use is not authorized</Typography>
          </Box>
        </Box>

        {/* Limitation of Liability */}
        <Box sx={{ mb: 6, p: 4, bgcolor: "#111", borderRadius: 2 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            5. Limitation of Liability
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            To the maximum extent permitted by law:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              Our service is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              We do not guarantee that our service will be uninterrupted, timely, secure, or error-free.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              We will not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or other intangible losses.
            </Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Our total liability for any claims arising from or related to our service will not exceed the amount you paid for the service during the six months preceding the claim.
            </Typography>
          </Box>
        </Box>

        {/* Dispute Resolution */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            6. Dispute Resolution
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            Any disputes arising from or related to these terms or our service will be resolved as follows:
          </Typography>
          <Box component="ol" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Informal Resolution:</strong> We encourage you to contact us first to seek an informal resolution to any disputes.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Mediation:</strong> If informal resolution is unsuccessful, the parties agree to attempt to resolve the dispute through mediation conducted by a mutually agreed-upon mediator.
            </Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Arbitration:</strong> If mediation is unsuccessful, any remaining disputes will be resolved through binding arbitration conducted in accordance with the rules of the American Arbitration Association.
            </Typography>
          </Box>
        </Box>

        {/* Governing Law */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            7. Governing Law
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
            }}
          >
            These terms and your use of our service are governed by and construed in accordance with the laws of [Your Jurisdiction] without regard to its conflict of law provisions. However, this will not deprive you of the protections afforded under consumer protection laws in your jurisdiction of residence.
          </Typography>
        </Box>

        {/* Changes to Terms */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            8. Changes to Terms
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
            }}
          >
            We may update these terms from time to time. We will notify you of any material changes by posting the new terms on our website or by sending you an email notification. Your continued use of our service after such changes constitutes your acceptance of the new terms. If you do not agree to the new terms, you must stop using our service.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 6, p: 4, bgcolor: "#111", borderRadius: 2 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            If you have any questions about our Legal Information, please contact us at:
          </Typography>
          <Box sx={{ color: "#7a7976", pl: 2 }}>
            <Typography sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Email: info@arkyn-tech.com</Typography>
            <Typography sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Phone: +421 952307969</Typography>
            <Typography sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Address: Kostolný rad 318/15 Kráľovský Chlmec 077 01</Typography>
          </Box>
        </Box>

        <Divider sx={{ bgcolor: "#333", mb: 4 }} />
        
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: "#71706b",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "14px",
            mb: 2
          }}
        >
          © 2025 Arkyn-tech. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}